import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  Paper,
  Alert,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import LoginBottomLinks from './LoginBottomLinks';
import { useSearchParams } from 'react-router-dom';

function LoginForm() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const forwardedError = searchParams.get('error');
    const emailAddress = searchParams.get('email');
    if (forwardedError !== null) {
      setError(true);
      if (forwardedError === 'timeout') {
        setErrorMessage('Login timed out, please try again.');
      } else if (forwardedError === 'auth') {
        setErrorMessage('Invalid username or password.');
      } else {
        setErrorMessage('Login failed, please try again.');
      }
    } else {
      if (emailAddress) {
        setError(false);
        setConfirmed(true);
        setUsername(emailAddress);
      }
    }
    setSearchParams({});
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(false);

    if (!username || !password) {
      setError(true);
      return;
    }

    try {
      // In development, use the proxied endpoint to avoid CORS issues
      const loginUrl = import.meta.env.DEV
        ? '/j_security_check'
        : `${import.meta.env.VITE_REACT_SERVER_HOST}/j_security_check`;

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          j_username: username,
          j_password: password,
        }),
        credentials: 'include', // Important for session cookies
      });

      // Check if login was successful
      if (
        response.status === 302 ||
        response.redirected ||
        (response.ok && response.url && response.url.includes('account'))
      ) {
        window.location.href = '/notification';
      } else if (response.status === 408) {
        console.error('Login request timed out');
        window.location.href = '/notification?error=timeout';
        setError(true);
      } else {
        if (response.status === 500) {
          setErrorMessage('Login failed. Please try again.');
          setPassword(''); // Clear password on error
        } else if (response.status === 401 || response.status === 403) {
          setErrorMessage('Invalid username or password.');
          setPassword(''); // Clear password on error
        } else {
          setErrorMessage('Login failed. Please try again.');
          setPassword(''); // Clear password on error
        }
        setError(true);
      }
    } catch (err) {
      console.error('Error during login:', err);
      setErrorMessage(
        typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message?: string }).message)
          : String(err)
      );

      setError(true);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: theme.palette.text.primary,
          }}
        >
          Email Notifications
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          <span style={{ fontStyle: 'italic', fontWeight: 'bold' }}>
            PASSAGE
          </span>{' '}
          email notifications allows users to get real-time traffic information
          for designated roadways throughout Lake County
        </Typography>

        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {error && (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                {errorMessage}
              </Alert>
            )}
            {confirmed && (
              <Alert
                severity="success"
                sx={{
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                Email Address Confirmed
              </Alert>
            )}

            <TextField
              size="small"
              type="text"
              name="j_username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              variant="outlined"
              required
              label="Email Address"
              fullWidth
              aria-label="Email Address"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              size="small"
              type="password"
              name="j_password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              fullWidth
              aria-label="Password"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="contained"
                type="submit"
                disabled={!username.trim() || !password.trim()}
                aria-label="Login Button"
              >
                Login
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Bottom Links */}
        <Box sx={{ mt: 4 }}>
          <LoginBottomLinks />
        </Box>
      </Box>
    </Box>
  );
}

export default LoginForm;
